
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from '@supabase/supabase-js';
import { usersApi } from "@/api/users";

// Public routes that don't require authentication and shouldn't trigger redirects
const PUBLIC_ROUTES = [
  '/',
  '/about',
  '/faq',
  '/contact',
  '/privacy-policy',
  '/terms-of-service',
  '/referral-terms',
  '/coming-soon'
];

interface UserProfile {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  country?: string;
}

interface AuthContextType {
  user: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithProvider: (provider: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Function to create basic user profile from Supabase user
  const createBasicUserProfile = (userId: string, fallbackUser: User): UserProfile => {
    
    return {
      id: userId,
      name: fallbackUser.user_metadata?.name || fallbackUser.email?.split('@')[0] || 'User',
      email: fallbackUser.email || '',
      avatarUrl: fallbackUser.user_metadata?.avatar_url || '',
    };
  };

  // Initialize auth and set up listener for auth state changes
  useEffect(() => {
    // Clear any cached user profile data when context initializes
    localStorage.removeItem('wabisabi_user');
    
    // Set up the auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        
        try {
          setSession(currentSession);
          
          if (currentSession?.user) {
            // Create basic user profile first
            const userProfile = createBasicUserProfile(currentSession.user.id, currentSession.user);
            
            setUser(userProfile);
            
            // Store user profile in localStorage for consistency across app
            localStorage.setItem('wabisabi_user', JSON.stringify(userProfile));
            
            // Handle navigation after login
            if (event === 'SIGNED_IN' && currentSession) {
              // Check if we're on the password reset page or if this is a password recovery event
              const currentPath = window.location.pathname;
              const isPasswordResetFlow = currentPath === '/create-new-password' || event === 'PASSWORD_RECOVERY';

              // Check if current page is a public page that shouldn't trigger redirect
              const isPublicPage = PUBLIC_ROUTES.includes(currentPath) || currentPath.startsWith('/ref/');

              // Check if user is already on a protected route (don't redirect on page reload)
              const protectedRoutes = ['/dashboard', '/people', '/memories', '/suggestions', '/profile', '/onboarding', '/contacts', '/add-contact'];
              const isOnProtectedRoute = protectedRoutes.some(route => currentPath.startsWith(route));

              // Don't redirect if we're in the password reset flow, on a public page, or already on a protected route
              if (!isPasswordResetFlow && !isPublicPage && !isOnProtectedRoute) {
                // Add a small delay to ensure session is fully established
                setTimeout(async () => {
                  try {
                    const userDetails = await usersApi.getMe();

                    if (!userDetails.completedOnboarding) {
                      navigate("/onboarding");
                    } else {
                      navigate("/dashboard");
                    }
                  } catch (error) {
                    // Default to onboarding if we can't fetch details
                    navigate("/onboarding");
                  }
                }, 500); // 500ms delay to let session propagate
              }
            }
          } else {
            setUser(null);
            localStorage.removeItem('wabisabi_user');
          }
        } catch (error) {
          setUser(null);
          localStorage.removeItem('wabisabi_user');
        } finally {
          setIsLoading(false);
        }
      }
    );

    // Get initial session (this will trigger the auth state change listener)
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        // The auth state change listener will handle the session
        if (!session) {
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
      }
    };

    getInitialSession();

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        throw error;
      }
      
      // The auth state change listener will handle the rest
      // Just show success message here
      if (data.session) {
        toast({
          title: "Login successful",
          description: "Welcome back!",
        });
      }
      
    } catch (error) {
      toast({
        title: "Login failed",
        description: (error as Error).message || "Please check your credentials and try again.",
        variant: "destructive",
      });
      throw error; // Re-throw so the Login page can handle it
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithProvider = async (provider: string) => {
    try {
      setIsLoading(true);
      
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider.toLowerCase() as 'google' | 'github' | 'gitlab' | 'bitbucket',
        options: {
          redirectTo: `${window.location.origin}/auth?provider=${provider}&redirect=onboarding`,
        }
      });
      
      if (error) {
        throw error;
      }
      
      // The redirect happens automatically, so we don't need to navigate
      
    } catch (error) {
      toast({
        title: "Login failed",
        description: `Could not authenticate with ${provider}. ${(error as Error).message}`,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const signup = async (name: string, email: string, password: string) => {
    try {
      setIsLoading(true);
      
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
          },
        },
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Account created",
        description: "Your account has been created successfully.",
      });
      
      // For development, we'll redirect to the profile page
      // In production, we might need to verify email first
      navigate("/profile?onboarding=true");
      
    } catch (error) {
      toast({
        title: "Signup failed",
        description: (error as Error).message || "Could not create your account. Please try again.",
        variant: "destructive",
      });
      throw error; // Re-throw so the Signup page can handle it
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      // Remove from localStorage
      localStorage.removeItem('wabisabi_user');
      setUser(null);
      
      navigate("/");
      toast({
        title: "Logged out",
        description: "You have been logged out successfully.",
      });
    } catch (error) {
      toast({
        title: "Logout failed",
        description: (error as Error).message || "Could not sign out. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        loginWithProvider,
        signup,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
